import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import {
  BillingExtraLineDTO,
  BillingParticipantBasis,
  EventCustomerBillingDTO,
} from '../../../dto/event/event-customer-billing.dto';

/** Shared dialog size for proformafaktura. */
export const DUNA_PROFORMA_DIALOG_CONFIG = {
  width: '900px',
  maxWidth: '96vw',
  maxHeight: '92vh',
  panelClass: 'proforma-dialog-panel',
};

interface ProformaForm {
  revenue_model: 'per_participant' | 'fixed_course';
  price: number;
  billing_participant_basis: BillingParticipantBasis;
  use_billing_participant_override: boolean;
  billing_participant_count_override: number;
  has_extra: boolean;
  extra_amount: number | null;
  extra_amount_comment: string;
}

@Component({
  selector: 'app-duna-customers-proforma-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDividerModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
  ],
  templateUrl: './duna-customers-proforma-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-customers-proforma-dialog.component.scss',
})
export class DunaCustomersProformaDialogComponent implements OnInit {
  form: ProformaForm = {
    revenue_model: 'fixed_course',
    price: 0,
    billing_participant_basis: 'registered',
    use_billing_participant_override: false,
    billing_participant_count_override: 0,
    has_extra: false,
    extra_amount: null,
    extra_amount_comment: '',
  };

  proforma: EventCustomerBillingDTO | null = null;
  priceExtrasLines: BillingExtraLineDTO[] = [];
  cateringExtrasLines: BillingExtraLineDTO[] = [];
  loading = true;
  saving = false;
  error: string | null = null;

  readonly lineColumns = ['label', 'quantity', 'unit_price', 'line_total'];

  constructor(
    private http: HttpClient,
    private dialogRef: MatDialogRef<DunaCustomersProformaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { row: EventCustomerBillingDTO }
  ) {}

  ngOnInit(): void {
    this.loadProforma();
  }

  get registeredCount(): number {
    return this.proforma?.registered_participant_count ?? 0;
  }

  get completedCount(): number {
    return this.proforma?.completed_participant_count ?? 0;
  }

  get billingCount(): number {
    if (this.form.billing_participant_basis === 'override') {
      return Math.max(
        0,
        Math.floor(this.form.billing_participant_count_override)
      );
    }
    if (this.form.billing_participant_basis === 'completed') {
      return this.completedCount;
    }
    return this.registeredCount;
  }

  get baseAmount(): number {
    if (this.form.revenue_model === 'per_participant') {
      return this.form.price * this.billingCount;
    }
    return this.form.price;
  }

  get extrasTotal(): number {
    const priceTotal = this.priceExtrasLines.reduce(
      (sum, line) => sum + this.lineTotal(line),
      0
    );
    const cateringTotal = this.cateringExtrasLines.reduce(
      (sum, line) => sum + this.lineTotal(line),
      0
    );
    return priceTotal + cateringTotal;
  }

  get legacyExtraAmount(): number {
    return this.form.has_extra ? this.form.extra_amount ?? 0 : 0;
  }

  get computedTotal(): number {
    return this.baseAmount + this.extrasTotal + this.legacyExtraAmount;
  }

  lineTotal(line: BillingExtraLineDTO): number {
    return Math.max(0, line.quantity) * Math.max(0, line.unit_price ?? 0);
  }

  formatKr(value: number | null | undefined): string {
    if (value == null) return '–';
    return value.toLocaleString('da-DK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }

  participantStatusLabel(status?: string): string {
    switch ((status ?? '').toLowerCase()) {
      case 'pending':
        return 'Tilmeldt';
      case 'shownup':
        return 'Mødt op';
      case 'completed':
        return 'Gennemført';
      case 'cancellation':
        return 'Afmeldt';
      case 'waitlist':
        return 'Venteliste';
      default:
        return status ?? '–';
    }
  }

  onBasisChange(): void {
    if (
      this.form.billing_participant_basis === 'override' &&
      !this.form.billing_participant_count_override
    ) {
      this.form.billing_participant_count_override = this.registeredCount;
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  loadProforma(): void {
    this.loading = true;
    this.error = null;
    this.http
      .get<EventCustomerBillingDTO>(
        `${environment.apiUrl}/organizations/customers/events/${this.data.row.guid}/proforma`
      )
      .subscribe({
        next: (proforma) => {
          this.proforma = proforma;
          this.initForm(proforma);
          this.priceExtrasLines = (proforma.price_extras_lines ?? []).map(
            (line) => ({ ...line })
          );
          this.cateringExtrasLines = (proforma.catering_extras_lines ?? []).map(
            (line) => ({ ...line })
          );
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading proforma:', err);
          this.error = this.extractError(
            err,
            'Kunne ikke hente proformafaktura.'
          );
          this.loading = false;
        },
      });
  }

  private initForm(row: EventCustomerBillingDTO): void {
    const hasOverride = row.billing_participant_count_override != null;
    const basis: BillingParticipantBasis =
      hasOverride || row.billing_participant_basis === 'override'
        ? 'override'
        : 'registered';
    this.form = {
      revenue_model: row.revenue_model ?? 'fixed_course',
      price: row.price ?? 0,
      billing_participant_basis: basis,
      use_billing_participant_override: hasOverride || basis === 'override',
      billing_participant_count_override: hasOverride
        ? (row.billing_participant_count_override as number)
        : row.registered_participant_count ?? 0,
      has_extra: row.extra_amount != null && row.extra_amount > 0,
      extra_amount: row.extra_amount ?? null,
      extra_amount_comment: row.extra_amount_comment ?? '',
    };
  }

  save(): void {
    this.saving = true;
    this.error = null;

    const payload: Record<string, unknown> = {
      revenue_model: this.form.revenue_model,
      price: this.form.price,
      extra_amount: this.form.has_extra ? this.form.extra_amount ?? 0 : null,
      extra_amount_comment: this.form.has_extra
        ? this.form.extra_amount_comment || null
        : null,
      price_extras: this.priceExtrasLines
        .filter((line) => line.quantity > 0)
        .map((line) => ({
          extra_option_id: line.extra_option_id,
          quantity: Math.max(0, Math.floor(line.quantity)),
          unit_price: Math.max(0, Number(line.unit_price ?? 0)),
        })),
      catering_extras: this.cateringExtrasLines
        .filter((line) => line.quantity > 0)
        .map((line) => ({
          extra_option_id: line.extra_option_id,
          quantity: Math.max(0, Math.floor(line.quantity)),
          unit_price: Math.max(0, Number(line.unit_price ?? 0)),
          line_id: line.line_id ?? undefined,
          comment: line.comment ?? undefined,
        })),
    };

    if (this.form.revenue_model === 'per_participant') {
      if (this.form.billing_participant_basis === 'override') {
        payload['billing_participant_basis'] = 'override';
        payload['billing_participant_count_override'] = Math.max(
          0,
          Math.floor(this.form.billing_participant_count_override)
        );
      } else {
        payload['billing_participant_basis'] =
          this.form.billing_participant_basis;
        payload['billing_participant_count_override'] = null;
      }
    }

    this.http
      .patch<EventCustomerBillingDTO>(
        `${environment.apiUrl}/organizations/customers/events/${this.data.row.guid}/billing`,
        payload
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err) => {
          console.error('Error saving proforma:', err);
          this.error = this.extractError(
            err,
            'Der opstod en fejl ved gemning.'
          );
          this.saving = false;
        },
      });
  }

  private extractError(err: unknown, fallback: string): string {
    const body = (err as { error?: unknown })?.error;
    if (typeof body === 'string' && body.trim().startsWith('<')) {
      return 'Serverfejl (PHP). Tjek at API er opdateret og kører uden fejl.';
    }
    if (typeof body === 'object' && body && 'message' in body) {
      return String((body as { message?: string }).message);
    }
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    return fallback;
  }
}
